import { Injectable, inject } from '@angular/core';
import { Borders, Workbook, Worksheet } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { RowInput, Styles } from 'jspdf-autotable';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { BCAReports, TableCell } from '../../models/lab-test';
import { PlusFractionResult } from '../../bca/models/bca';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFCompositionRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    commonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    commonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStyles = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    commonHeadStylesWithoutBorder = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStylesWithoutBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    async GenerateCompositionalAnalysis(workbook: Workbook, worksheetName: string, bcaExp: BCAReports, wellName: string, sampleName: string, subHeader: string, recombined = "") {
        try {
            const compAnalysisWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(compAnalysisWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1, //page 1
                fitToHeight: 1, // automatic
                scale: 67,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            compAnalysisWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            compAnalysisWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });


            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(compAnalysisWorksheet, subHeader, reportConstants.d1, reportConstants.k1);

            compAnalysisWorksheet.getColumn(reportConstants.c).width = 15;
            compAnalysisWorksheet.getColumn(reportConstants.fChar).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.i).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.a).width = 3;
            compAnalysisWorksheet.getColumn(reportConstants.l).width = 3;

            compAnalysisWorksheet.getRow(4).height = 20;
            compAnalysisWorksheet.getRow(5).height = 20;
            compAnalysisWorksheet.mergeCells(reportConstants.b4, reportConstants.k4);
            const textReportTitleCell = compAnalysisWorksheet.getCell(reportConstants.b4);
            textReportTitleCell.value = recombined === "" ? wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis : wellName + ' ' + recombined + ' ' + reportConstants.compositionalAnalysis;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            compAnalysisWorksheet.mergeCells(reportConstants.b5, reportConstants.k5);
            const textReportSubtitleCell = compAnalysisWorksheet.getCell(reportConstants.b5);
            textReportSubtitleCell.value = reportConstants.compositionData;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            const tableStartRow = 7;
            compAnalysisWorksheet.getRow(tableStartRow).height = 20;
            compAnalysisWorksheet.getRow(tableStartRow + 1).height = 20;

            compAnalysisWorksheet.mergeCells(`D${tableStartRow}`, `E${tableStartRow}`);
            const textFlashedLiquidCell = compAnalysisWorksheet.getCell(`D${tableStartRow}`);
            textFlashedLiquidCell.value = reportConstants.flashedLiquid;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            const headerCellH = compAnalysisWorksheet.getCell(`F${tableStartRow}`);
            headerCellH.border = borderStyle;
            compAnalysisWorksheet.mergeCells(`G${tableStartRow}`, `H${tableStartRow}`);
            const textFlashedGasCell = compAnalysisWorksheet.getCell(`G${tableStartRow}`);
            textFlashedGasCell.value = reportConstants.flashedGas;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            const headerCellK = compAnalysisWorksheet.getCell(`I${tableStartRow}`);
            headerCellK.border = borderStyle;
            compAnalysisWorksheet.mergeCells(`J${tableStartRow}`, `K${tableStartRow}`);
            const textWholeFluidCell = compAnalysisWorksheet.getCell(`J${tableStartRow}`);
            textWholeFluidCell.value = reportConstants.wholeFluid;
            textWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textWholeFluidCell.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`B${tableStartRow + 1}`, `C${tableStartRow + 1}`);
            const textComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 1}`);
            textComponentCell.value = reportConstants.component;
            textComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textComponentCell.border = borderStyle;

            const textLiquidWeightPercentCell = compAnalysisWorksheet.getCell(`D${tableStartRow + 1}`);
            textLiquidWeightPercentCell.value = reportConstants.weight;
            textLiquidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidWeightPercentCell.border = borderStyle;

            const textLiquidMolePercentCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 1}`);
            textLiquidMolePercentCell.value = reportConstants.molePercent;
            textLiquidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidMolePercentCell.border = borderStyle;

            const subHeaderCellH = compAnalysisWorksheet.getCell(`F${tableStartRow + 1}`);
            subHeaderCellH.border = borderStyle;

            const textGasWeightPercentCell = compAnalysisWorksheet.getCell(`G${tableStartRow + 1}`);
            textGasWeightPercentCell.value = reportConstants.weight;
            textGasWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasWeightPercentCell.border = borderStyle;

            const textGasMolePercentCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 1}`);
            textGasMolePercentCell.value = reportConstants.molePercent;
            textGasMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasMolePercentCell.border = borderStyle;

            const subHeaderCellK = compAnalysisWorksheet.getCell(`I${tableStartRow + 1}`);
            subHeaderCellK.border = borderStyle;

            const textFluidWeightPercentCell = compAnalysisWorksheet.getCell(`J${tableStartRow + 1}`);
            textFluidWeightPercentCell.value = reportConstants.weight;
            textFluidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidWeightPercentCell.border = borderStyle;

            const textFluidMolePercentCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 1}`);
            textFluidMolePercentCell.value = reportConstants.molePercent;
            textFluidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidMolePercentCell.border = borderStyle;

            const formatedData = this._reportGeneralUtilityService.FormatTableDataComposition(bcaExp.reportCompositionData);

            for (let i = 0; i < formatedData.length; i++) {
                const row = formatedData[i];
                const component = row[0];
                let minusCount = 0;

                const isObject = component && typeof component === 'object' && component !== null && 'rowSpan' in component && 'content' in component;

                if (isObject && component.rowSpan > 1) {
                    compAnalysisWorksheet.mergeCells(`B${tableStartRow + 2 + i}:B${tableStartRow + 2 + i + component.rowSpan - 1}`);
                }

                if (isObject) {
                    const rowComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 2 + i}`);
                    rowComponentCell.value = component.content;
                    rowComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                    rowComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    rowComponentCell.border = borderStyle;
                }
                else {
                    minusCount = 1;
                }

                const textComponentNameCell = compAnalysisWorksheet.getCell(`C${tableStartRow + 2 + i}`);
                textComponentNameCell.value = this.GetCellValue(row[1 - minusCount]);
                textComponentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                textComponentNameCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textComponentNameCell.border = borderStyle;

                const liquidWeightCell = compAnalysisWorksheet.getCell(`D${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(liquidWeightCell, row[2 - minusCount]);
                liquidWeightCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                liquidWeightCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                liquidWeightCell.border = borderStyle;

                const liquidMoleCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(liquidMoleCell, row[3 - minusCount]);
                liquidMoleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                liquidMoleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                liquidMoleCell.border = borderStyle;

                const textCellH = compAnalysisWorksheet.getCell(`F${tableStartRow + 2 + i}`);
                textCellH.border = borderStyle;

                const gasWeightCell = compAnalysisWorksheet.getCell(`G${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(gasWeightCell, row[5 - minusCount]);
                gasWeightCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                gasWeightCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                gasWeightCell.border = borderStyle;

                const gasMoleCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(gasMoleCell, row[6 - minusCount]);
                gasMoleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                gasMoleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                gasMoleCell.border = borderStyle;

                const textCellK = compAnalysisWorksheet.getCell(`I${tableStartRow + 2 + i}`);
                textCellK.border = borderStyle;

                const fluidWeightCell = compAnalysisWorksheet.getCell(`J${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(fluidWeightCell, row[8 - minusCount]);
                fluidWeightCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                fluidWeightCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                fluidWeightCell.border = borderStyle;

                const fluidMoleCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(fluidMoleCell, row[9 - minusCount]);
                fluidMoleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                fluidMoleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                fluidMoleCell.border = borderStyle;

                compAnalysisWorksheet.getColumn(reportConstants.c).width = 24;
                compAnalysisWorksheet.getColumn(reportConstants.d).width = 12;
                compAnalysisWorksheet.getColumn(reportConstants.e).width = 12;
                compAnalysisWorksheet.getColumn(reportConstants.gColumn).width = 12;
                compAnalysisWorksheet.getColumn(reportConstants.h).width = 12;
                compAnalysisWorksheet.getColumn(reportConstants.j).width = 12;
                compAnalysisWorksheet.getColumn(reportConstants.k).width = 12;
            }

            
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }
    CompositionalAnalysis(doc: jsPDF, marginSize: number, bcaExp: BCAReports, wellName: string, sampleName: string) {
        if (bcaExp !== undefined && bcaExp !== null) {
            const compFlashGOR = bcaExp?.report?.compositionFlashCalcInput;
            const plusFracArray = bcaExp?.report?.plusFractionPropertyArray;
            const compositionFlash = bcaExp?.bcaData?.bcaMeasuredCompositionFlash;
            const wholeFluidPropertyArray = bcaExp?.report?.wholeFluidPropertyArray;
            const volumetricFlash = bcaExp?.bcaData?.bcaMeasuredVolumetricFlash;
            const volFlashGOR = bcaExp?.report?.volumetricFlashCalcInput;
            const compositionalFlashPressure = bcaExp?.report?.compositionalFlashPressure;
            const volumetricFlashPressure = bcaExp?.report?.volumetricFlashPressure;
            marginSize = 30;
            const formatedData = this._reportGeneralUtilityService.FormatTableDataComposition(bcaExp?.reportCompositionData);
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;
            doc.text(wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis, marginSize, currentY);
            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.compositionData, marginSize, currentY);
            currentY += 5;
            let tableStartY = currentY;
            autoTable(doc, {
                head: [
                    [
                        {
                            content: '',
                            colSpan: 2,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        { content: reportConstants.flashedLiquid, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.flashedGas, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.wholeFluid, colSpan: 2, styles: { halign: reportConstants.center } }
                    ],
                    [
                        {
                            content: reportConstants.component,
                            colSpan: 2,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                    ]
                ],
                body: formatedData as RowInput[],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: {
                    fontSize: 8,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    lineColor: [0, 0, 0],
                    lineWidth: 0
                },
                headStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0 // Remove border
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                columnStyles: {
                    4: { cellWidth: 1 },
                    7: { cellWidth: 1 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else if (section === reportConstants.head && row.index != 1) {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    else if (section === reportConstants.body) {
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Ã¢Å“â€¦ LEFT border
                        doc.line(x, y, x, y + h);
                        // Ã¢Å“â€¦ RIGHT border
                        doc.line(x + w, y, x + w, y + h);
                        // Ã¢Å“â€¦ BOTTOM border (draw ONCE per row)
                        doc.line(x, y + h, x + w, y + h);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5); // first bottom line
                        doc.line(x, y + h, x + w, y + h);             // second bottom line
                        doc.line(x, y, x, y + h);                     // left
                        doc.line(x + w, y, x + w, y + h);             // right
                        doc.line(x, y, x + w, y);                     // top
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            /*Single Stage Flash/GOR Data*/
            doc.addPage();
            currentY = 30;

            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis, marginSize, currentY);
            currentY += 5;

            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.singleStageFlashGORData, marginSize, currentY);
            currentY += 10;

            tableStartY = currentY;
            doc.setFont(reportConstants.eMprintCondensedBold);
            marginSize += 10;

            doc.text(reportConstants.compositionalFlash, marginSize, tableStartY);
            tableStartY += 5;

            doc.setFontSize(8);
            doc.text(`${reportConstants.flashConditionsExtra} ${compositionalFlashPressure} ${reportConstants.psia120}`, marginSize + 5, tableStartY);
            tableStartY += 3;
            doc.setFont(reportConstants.eMprintCondensedRegular);

            /*grid Flash*/
            autoTable(doc, {
                head: [],
                body: [
                    ['', compFlashGOR?.gorscfstb ?? ""],
                    ['', compFlashGOR?.lFormVolFactor ?? ""]
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: this.commonBodyStyles as Partial<Styles>,
                tableWidth: doc.internal.pageSize.getWidth() * 0.3,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 }
                },
                didDrawCell: function (data) {
                    if (data.column.index === 0 && data.row.index === 0) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.gor;
                        const subscript = reportConstants.gorThree;
                        const unit = reportConstants.gorUnit;

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }

                    if (data.column.index === 0 && data.row.index === 1) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.formationVolFactorPDF;
                        const subscript = reportConstants.formationVolFactorSubscript;
                        const unit = reportConstants.formationVolFactorUnit;

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4;
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                }
            });


            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.calculatedPlusFractionProperties, marginSize, tableStartY);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            tableStartY += 5;
            /*grid C36+*/
            autoTable(doc, {
                /* C36+ is a predefined component name(label)*/
                head: [[reportConstants.c36Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                body: [
                    [reportConstants.molePercent, plusFracArray?.[0]?.molePercentFlashedLiquid?.toFixed(2) ?? '', reportConstants.dash, plusFracArray?.[0]?.molePercentFlashedWholeFluid?.toFixed(2) ?? ''],
                    [reportConstants.molWeight, plusFracArray?.[0]?.molWeightFlashedLiquid ?? '', reportConstants.dash, plusFracArray?.[0]?.molWeightFlashedWholeFluid ?? ''],
                    ['', plusFracArray?.[0]?.specificGravityFlashedLiquid?.toFixed(3) ?? '', reportConstants.dash, plusFracArray?.[0]?.specificGravityFlashedWholeFluid?.toFixed(3) ?? '']
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 }
                },
                didParseCell: function (data) {
                    // Ã¢Å“â€¦ Apply bold only to header cell C36+
                    if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    const lw = reportConstants.tableLinewidth;
                    const snap = (v: number) => Math.round(v) + lw / 2;
                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right

                    }
                    else if (section === reportConstants.body && row.index === 7) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);
                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Ã¢Å“â€¦ LEFT border
                        doc.line(x, y, x, y + h);
                        // Ã¢Å“â€¦ RIGHT border
                        doc.line(x + w, y, x + w, y + h);
                        // Ã¢Å“â€¦ BOTTOM border (draw ONCE per row)
                        doc.line(x, y + h, x + w, y + h);
                        if (section === 'head') {
                            doc.line(x, y, x + w, y);
                        }
                        if (section === 'body' && row.index === 0 && column.index === 0) {
                            doc.line(x, y, x + w, y);
                        }
                    }

                    if (data.column.index === 0 && data.row.index === 2) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.specificGravityPDF;
                        const subscript = reportConstants.specificGravitySubscript;
                        const unit = '';

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            /*grid C20+*/
            autoTable(doc, {
                head: [[reportConstants.c20Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                body: [
                    [reportConstants.molePercent, plusFracArray?.[1]?.molePercentFlashedLiquid?.toFixed(2) ?? "", reportConstants.dash, plusFracArray?.[1]?.molePercentFlashedWholeFluid?.toFixed(2) ?? ''],
                    [reportConstants.mwgMol, plusFracArray?.[1]?.molWeightFlashedLiquid ?? "", reportConstants.dash, plusFracArray?.[1]?.molWeightFlashedWholeFluid ?? ''],
                    ['', plusFracArray?.[1]?.specificGravityFlashedLiquid?.toFixed(3) ?? '', reportConstants.dash, plusFracArray?.[1]?.specificGravityFlashedWholeFluid?.toFixed(3) ?? '']
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 }
                },
                didParseCell: function (data) {
                    // Ã¢Å“â€¦ Apply bold only to header cell C36+
                    if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    const lw = reportConstants.tableLinewidth;
                    const snap = (v: number) => Math.round(v) + lw / 2;
                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right

                    }
                    else if (section === reportConstants.body && row.index === 7) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);
                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Ã¢Å“â€¦ LEFT border
                        doc.line(x, y, x, y + h);
                        // Ã¢Å“â€¦ RIGHT border
                        doc.line(x + w, y, x + w, y + h);
                        // Ã¢Å“â€¦ BOTTOM border (draw ONCE per row)
                        doc.line(x, y + h, x + w, y + h);
                        if (section === 'head') {
                            doc.line(x, y, x + w, y);
                        }
                        if (section === 'body' && row.index === 0 && column.index === 0) {
                            doc.line(x, y, x + w, y);
                        }
                    }

                    if (data.column.index === 0 && data.row.index === 2) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.specificGravityPDF;
                        const subscript = reportConstants.specificGravitySubscript;
                        const unit = '';

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            /*grid C11+*/
            autoTable(doc, {
                head: [[reportConstants.c11Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                body: [
                    [reportConstants.molePercent, plusFracArray?.[2]?.molePercentFlashedLiquid?.toFixed(2) ?? '', reportConstants.dash, plusFracArray?.[2]?.molePercentFlashedWholeFluid?.toFixed(2) ?? ''],
                    [reportConstants.mwgMol, plusFracArray?.[2]?.molWeightFlashedLiquid ?? '', reportConstants.dash, plusFracArray?.[2]?.molWeightFlashedWholeFluid ?? ''],
                    ['', plusFracArray?.[2]?.specificGravityFlashedLiquid?.toFixed(3) ?? '', reportConstants.dash, plusFracArray?.[2]?.specificGravityFlashedWholeFluid?.toFixed(3) ?? '']
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 }
                },
                didParseCell: function (data) {
                    // Ã¢Å“â€¦ Apply bold only to header cell C36+
                    if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    const lw = reportConstants.tableLinewidth;
                    const snap = (v: number) => Math.round(v) + lw / 2;
                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right

                    }
                    else if (section === reportConstants.body && row.index === 7) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);
                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Ã¢Å“â€¦ LEFT border
                        doc.line(x, y, x, y + h);
                        // Ã¢Å“â€¦ RIGHT border
                        doc.line(x + w, y, x + w, y + h);
                        // Ã¢Å“â€¦ BOTTOM border (draw ONCE per row)
                        doc.line(x, y + h, x + w, y + h);
                        if (section === 'head') {
                            doc.line(x, y, x + w, y);
                        }
                        if (section === 'body' && row.index === 0 && column.index === 0) {
                            doc.line(x, y, x + w, y);
                        }
                    }

                    if (data.column.index === 0 && data.row.index === 2) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.specificGravityPDF;
                        const subscript = reportConstants.specificGravitySubscript;
                        const unit = '';

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            /*grid C7+*/
            autoTable(doc, {
                head: [[reportConstants.c7Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                body: [
                    [reportConstants.molePercent, plusFracArray?.[3]?.molePercentFlashedLiquid?.toFixed(2) ?? '', plusFracArray?.[3]?.molePercentFlashedGas?.toFixed(2) ?? '', plusFracArray?.[3]?.molePercentFlashedWholeFluid?.toFixed(2) ?? ''],
                    [reportConstants.mwgMol, plusFracArray?.[3]?.molWeightFlashedLiquid ?? '', plusFracArray?.[3]?.molWeightFlashedGas ?? '', plusFracArray?.[3]?.molWeightFlashedWholeFluid ?? ''],
                    ['', plusFracArray?.[3]?.specificGravityFlashedLiquid?.toFixed(3) ?? '', plusFracArray?.[3]?.specificGravityFlashedGas?.toFixed(3) ?? '', plusFracArray?.[3]?.specificGravityFlashedWholeFluid?.toFixed(3) ?? '']
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 }
                },
                didParseCell: function (data) {
                    // Ã¢Å“â€¦ Apply bold only to header cell C36+
                    if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    const lw = reportConstants.tableLinewidth;
                    const snap = (v: number) => Math.round(v) + lw / 2;
                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right

                    }
                    else if (section === reportConstants.body && row.index === 7) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);
                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Ã¢Å“â€¦ LEFT border
                        doc.line(x, y, x, y + h);
                        // Ã¢Å“â€¦ RIGHT border
                        doc.line(x + w, y, x + w, y + h);
                        // Ã¢Å“â€¦ BOTTOM border (draw ONCE per row)
                        doc.line(x, y + h, x + w, y + h);
                        if (section === 'head') {
                            doc.line(x, y, x + w, y);
                        }
                        if (section === 'body' && row.index === 0 && column.index === 0) {
                            doc.line(x, y, x + w, y);
                        }
                    }
                    if (data.column.index === 0 && data.row.index === 2) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.specificGravityPDF;
                        const subscript = reportConstants.specificGravitySubscript;
                        const unit = '';

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.flashedSampleProperties, marginSize, tableStartY);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            tableStartY += 5;
            const oilAPIGravityComp = ((reportConstants.oilFlashValue1 / (compositionFlash?.densityOfDeadOil / reportConstants.oilFlashValue2)) - reportConstants.oilFlashValue3).toFixed(1)
            /*grid flashedLiquid*/
            autoTable(doc, {
                head: [['', reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                body: [
                    [reportConstants.calculatedAvgMW, wholeFluidPropertyArray?.[0]?.flashedLiquid ?? '', wholeFluidPropertyArray?.[0]?.flashedGas ?? '', wholeFluidPropertyArray?.[0]?.wholeFluid ?? ''],
                    [reportConstants.measuredAvgMW, wholeFluidPropertyArray?.[1]?.flashedLiquid ?? '', '', ''],
                    ['', wholeFluidPropertyArray?.[2]?.flashedLiquid ?? '', '', ''],
                    [reportConstants.measuredDensity, wholeFluidPropertyArray?.[3]?.flashedLiquid ?? '', '', ''],
                    [reportConstants.moleFraction, Number(wholeFluidPropertyArray?.[4]?.flashedLiquid)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[4]?.flashedGas)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[4]?.wholeFluid)?.toFixed(4) ?? ''],
                    [reportConstants.massFraction, Number(wholeFluidPropertyArray?.[5]?.flashedLiquid)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[5]?.flashedGas)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[5]?.wholeFluid)?.toFixed(4) ?? ''],
                    ['', '', wholeFluidPropertyArray?.[6]?.flashedGas ?? '', ''],
                    [reportConstants.oilAPIGravity, oilAPIGravityComp],
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: {
                    fontSize: 8,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    lineColor: [0, 0, 0],
                    lineWidth: 0
                },
                headStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0 // Remove border
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 }
                },
                didParseCell: function (data) {
                    // Ã¢Å“â€¦ Apply bold only to header cell C36+
                    if (data.section === reportConstants.head) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;

                    const lw = reportConstants.tableLinewidth;
                    const snap = (v: number) => Math.round(v) + lw / 2;

                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else if (section === reportConstants.body && row.index === 7) {
                        const x = snap(cell.x);
                        const y = snap(cell.y);
                        const w = Math.round(cell.width);
                        const h = Math.round(cell.height);
                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        // Ã¢Å“â€¦ LEFT border
                        doc.line(x, y, x, y + h);
                        // Ã¢Å“â€¦ RIGHT border
                        doc.line(x + w, y, x + w, y + h);
                        // Ã¢Å“â€¦ BOTTOM border (draw ONCE per row)
                        doc.line(x, y + h, x + w, y + h);
                        if (section === 'head') {
                            doc.line(x, y, x + w, y);
                        }
                        if (section === 'body' && row.index === 0 && column.index === 0) {
                            doc.line(x, y, x + w, y);
                        }
                    }
                    if (data.column.index === 0 && data.row.index === 2) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.calculatedDensityPDF;
                        const subscript = reportConstants.specificGravitySubscript;
                        const unit = reportConstants.calculatedDensityUnit;

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                    if (data.column.index === 0 && data.row.index === 6) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.gasGravityPDF;
                        const subscript = reportConstants.gasGravitySubscript;
                        const unit = '';

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;


            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.setFontSize(10);
            doc.text(reportConstants.volumetricFlash, marginSize, tableStartY);
            tableStartY += 5;
            doc.setFontSize(8);
            doc.text(`${reportConstants.flashConditionsExtra}: ${volumetricFlashPressure} ${reportConstants.pisa80}`, marginSize + 5, tableStartY);
            tableStartY += 3;
            /*Grid gor*/
            autoTable(doc, {
                head: [],
                body: [
                    ['', volFlashGOR?.gorscfstb ?? ''],
                    ['', volFlashGOR?.lFormVolFactor ?? ''],
                    [reportConstants.measuredDensityWithoutF, volumetricFlash?.densityOfDeadOil?.toFixed(4) ?? ''],
                    [reportConstants.oilAPIGravity, volFlashGOR?.oilAPIGravity ?? '']
                ],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 25 }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;

                    // Remove border for "ComponentName" cell (top-left header cell)
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);         // top
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    } else if (section === reportConstants.body && row.index === 3) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        // Overdraw borders with white lines to "remove" them
                        doc.setDrawColor(255, 255, 255); // White
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y + h, x + w, y + h); // bottom
                        doc.line(x, y, x, y + h);         // left
                        doc.line(x + w, y, x + w, y + h); // right
                    }
                    else {
                        // Draw the border for other cells
                        doc.setDrawColor(0, 0, 0); // Black
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    if (data.column.index === 0 && data.row.index === 0) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.gor;
                        const subscript = reportConstants.gorThree;
                        const unit = reportConstants.gorUnit;

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                    if (data.column.index === 0 && data.row.index === 1) {
                        const fontSize = 8;
                        const subFontSize = 6;

                        const baseText = reportConstants.formationVolFactorPDF;
                        const subscript = reportConstants.formationVolFactorSubscript;
                        const unit = reportConstants.formationVolFactorUnit;

                        doc.setFontSize(fontSize);
                        const baseTextWidth = doc.getTextWidth(baseText);

                        doc.setFontSize(subFontSize);
                        const subscriptWidth = doc.getTextWidth(subscript);

                        doc.setFontSize(fontSize);
                        const unitWidth = doc.getTextWidth(unit);

                        const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                        const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                        const textHeight = fontSize * 0.3528;
                        const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                        doc.setFontSize(fontSize);
                        doc.text(baseText, baseX, baseY);

                        doc.setFontSize(subFontSize);
                        doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                        doc.setFontSize(fontSize);
                        doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                    }
                },
                tableWidth: doc.internal.pageSize.getWidth() * 0.3
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.setFontSize(8);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.specificGravity, marginSize, tableStartY);
            tableStartY += 5;
            doc.text(reportConstants.gasGravityAir, marginSize, tableStartY);
            tableStartY += 5;
            doc.text(reportConstants.gorMeasurementCorrected, marginSize, tableStartY);
            tableStartY += 5;
            doc.text(reportConstants.formationVolumeFactor, marginSize, tableStartY);

            doc.addPage();

            currentY = 25;
            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis, marginSize - 20, currentY);
            currentY += 5;

            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.materialBalanceCheckAndHoffmanPlot, marginSize - 20, currentY);
            currentY += 5;
            doc.setFontSize(10);
            const pageWidth = doc.internal.pageSize.getWidth();
            const imageWidth = pageWidth * 0.8;

            const xPos = (pageWidth - imageWidth) / 2; // Center horizontally
            let yPos = currentY; // Starting Y position
            // First image

            const graphWidth = 150;
            const graphHeight = 115;

            doc.addImage(
                bcaExp.graphData[0],
                reportConstants.jpeg,
                xPos + 7,
                yPos,
                graphWidth,
                graphHeight,
                undefined,
                'FAST'
            );

            yPos += graphHeight + 10;

            doc.addImage(
                bcaExp.graphData[1],
                reportConstants.jpeg,
                xPos + 7,
                yPos,
                graphWidth,
                graphHeight,
                undefined,
                'FAST'
            );

        }
    }

    async GenerateMaterialBalanceCheckAndHoffmanPlot(workbook: Workbook, worksheetName: string, bcaExp: BCAReports, wellName: string, sampleName: string, subHeader: string, recombined = "") {
        try {
            const graphsWorksheet = workbook.addWorksheet(worksheetName);

            graphsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(graphsWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0, //page 1
                fitToHeight: 0, // automatic
                scale: 61,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            graphsWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(graphsWorksheet, subHeader, reportConstants.d1, reportConstants.k1);

            graphsWorksheet.getRow(4).height = 25;
            graphsWorksheet.getRow(5).height = 25;

            graphsWorksheet.getColumn(12).width = 15;

            graphsWorksheet.mergeCells(reportConstants.a4, reportConstants.h4);
            const textReportTitleCell = graphsWorksheet.getCell(reportConstants.a4);
            textReportTitleCell.value = recombined == "" ? wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis : wellName + ' ' + recombined + ' ' + reportConstants.compositionalAnalysis;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            graphsWorksheet.mergeCells(reportConstants.a5, reportConstants.h5);
            const textReportSubtitleCell = graphsWorksheet.getCell(reportConstants.a5);
            textReportSubtitleCell.value = reportConstants.materialBalanceCheckAndHoffmanPlot;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            const materialBalanceId = workbook.addImage({
                base64: bcaExp.graphData[0],
                extension: reportConstants.png,
            });

            graphsWorksheet.mergeCells(reportConstants.b9, reportConstants.l34);
            graphsWorksheet.addImage(materialBalanceId, {
                tl: { col: 1, row: 6 },
                ext: { width: 700, height: 500 }
            });

            const hoffmanPlotId = workbook.addImage({
                base64: bcaExp.graphData[1],
                extension: reportConstants.png,
            });

            graphsWorksheet.mergeCells(reportConstants.b38, reportConstants.l50);
            graphsWorksheet.addImage(hoffmanPlotId, {
                tl: { col: 1, row: 35 },
                ext: { width: 700, height: 500 }
            });
            const rowNo = 70;
            this.AddOuterBorder(graphsWorksheet, 3, rowNo, 1, 12);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateSingleStageFlashGORData(workbook: Workbook, worksheetName: string, bcaExp: BCAReports, wellName: string, sampleName: string, subHeader: string, recombined = "") {
        try {
            const singleStageFlashGORWorksheet = workbook.addWorksheet(worksheetName);
            const compFlashGOR = bcaExp?.report?.compositionFlashCalcInput;
            const plusFracArray = bcaExp?.report?.plusFractionPropertyArray ?? [];
            const wholeFluidPropertyArray = bcaExp?.report?.wholeFluidPropertyArray;
            const volumetricFlash = bcaExp?.bcaData?.bcaMeasuredVolumetricFlash;
            const volFlashGOR = bcaExp?.report?.volumetricFlashCalcInput;

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            singleStageFlashGORWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(singleStageFlashGORWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1, //page 1
                fitToHeight: 1, // automatic
                scale: 75,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            singleStageFlashGORWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(singleStageFlashGORWorksheet, subHeader, reportConstants.c1, reportConstants.j1);

            singleStageFlashGORWorksheet.getColumn(reportConstants.a).width = 5;

            singleStageFlashGORWorksheet.getRow(3).height = 20;
            singleStageFlashGORWorksheet.getRow(4).height = 20;
            singleStageFlashGORWorksheet.getRow(5).height = 20;

            singleStageFlashGORWorksheet.mergeCells(reportConstants.a3, reportConstants.h3);
            const textReportTitleCell = singleStageFlashGORWorksheet.getCell(reportConstants.a3);
            textReportTitleCell.value = recombined === "" ? wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis : wellName + ' ' + recombined + ' ' + reportConstants.compositionalAnalysis;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.mergeCells(reportConstants.a4, reportConstants.e4);
            const textReportSubtitleCell = singleStageFlashGORWorksheet.getCell(reportConstants.a4);
            textReportSubtitleCell.value = reportConstants.singleStageFlashGORData;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b5, reportConstants.e5);
            const textCompositionalFlashCell = singleStageFlashGORWorksheet.getCell(reportConstants.b5);
            textCompositionalFlashCell.value = reportConstants.compositionalFlash;
            textCompositionalFlashCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textCompositionalFlashCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b7, reportConstants.g7);
            const textFlashConditionsCompCell = singleStageFlashGORWorksheet.getCell(reportConstants.b7);
            textFlashConditionsCompCell.value = reportConstants.flashConditions;
            textFlashConditionsCompCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashConditionsCompCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.bottom };

            let rowNo = 9;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.gasOilRatio, compFlashGOR?.gorscfstb ?? "", rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.formationVolFactor, compFlashGOR?.lFormVolFactor ?? "", rowNo, borderStyle as Partial<Borders>);
            rowNo += 2;
            singleStageFlashGORWorksheet.getRow(rowNo).height = 10;
            singleStageFlashGORWorksheet.getRow(rowNo + 1).height = 10;
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textCalcPlusFractionCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textCalcPlusFractionCell.value = reportConstants.calculatedPlusFractionProperties;
            textCalcPlusFractionCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textCalcPlusFractionCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo += 2;

            plusFracArray.forEach((plusFracObj: PlusFractionResult) => {
                PopulateCalcPlusFractionData(singleStageFlashGORWorksheet, plusFracObj, rowNo, borderStyle);
                rowNo += 5;
            });

            singleStageFlashGORWorksheet.getRow(rowNo).height = 10;
            singleStageFlashGORWorksheet.getRow(rowNo + 1).height = 10;
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textFlashedSamplePropertiesCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textFlashedSamplePropertiesCell.value = reportConstants.flashedSampleProperties;
            textFlashedSamplePropertiesCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedSamplePropertiesCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            PopulateFlashedFluidHeaders(singleStageFlashGORWorksheet, '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;

            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.calculatedAvgMW, wholeFluidPropertyArray?.[0]?.flashedLiquid ?? '', wholeFluidPropertyArray?.[0]?.flashedGas ?? '', wholeFluidPropertyArray?.[0]?.wholeFluid ?? '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.measuredAvgMW, wholeFluidPropertyArray?.[1]?.flashedLiquid ?? '', '', '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.calculatedDensity, wholeFluidPropertyArray?.[2]?.flashedLiquid ?? '', '', '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.measuredDensity, wholeFluidPropertyArray?.[3]?.flashedLiquid ?? '', '', '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.moleFraction, Number(wholeFluidPropertyArray?.[4]?.flashedLiquid)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[4]?.flashedGas)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[4]?.wholeFluid)?.toFixed(4) ?? '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.massFraction, Number(wholeFluidPropertyArray?.[5]?.flashedLiquid)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[5]?.flashedGas)?.toFixed(4) ?? '', Number(wholeFluidPropertyArray?.[5]?.wholeFluid)?.toFixed(4) ?? '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.gasGravity, '', wholeFluidPropertyArray?.[6]?.flashedGas ?? '', '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.oilAPIGravity, compFlashGOR?.oilAPIGravity ?? '', '', '', rowNo);
            rowNo += 2;

            singleStageFlashGORWorksheet.getRow(rowNo).height = 10;
            singleStageFlashGORWorksheet.getRow(rowNo + 1).height = 10;
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textVolumetricFlashCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textVolumetricFlashCell.value = reportConstants.volumetricFlash;
            textVolumetricFlashCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textVolumetricFlashCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo += 2;

            singleStageFlashGORWorksheet.getRow(rowNo).height = 10;
            singleStageFlashGORWorksheet.getRow(rowNo + 1).height = 10;
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textFlashConditionsVolumeCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textFlashConditionsVolumeCell.value = `${reportConstants.flashConditionsExtra} ${bcaExp?.report?.volumetricFlashPressure ?? ''} ${reportConstants.psiaAnd80F}`;
            textFlashConditionsVolumeCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashConditionsVolumeCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.bottom };
            rowNo += 2;

            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.gasOilRatio, volFlashGOR?.gorscfstb ?? '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.formationVolFactor, volFlashGOR?.lFormVolFactor ?? '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.measuredDensityWithoutF, volumetricFlash?.densityOfDeadOil?.toFixed(4)?.toString() ?? '', rowNo, borderStyle as Partial<Borders>);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.oilAPIGravity, volFlashGOR?.oilAPIGravity ?? '', rowNo);
            rowNo += 2;

            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.specificGravity);
            rowNo++;
            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.gasGravityAir);
            rowNo++;
            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.gorMeasurementCorrected);
            rowNo++;
            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.formationVolumeFactor);
            rowNo += 2;

            this.AddOuterBorder(singleStageFlashGORWorksheet, 3, rowNo, 1, 10);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function PopulateComment(singleStageFlashGORWorksheet: Worksheet, rowNo: number, comments: string) {
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.h + rowNo);
            const textCommentsCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textCommentsCell.value = comments;
            textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: 9, bold: false };
            textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        }

        function PopulateFlashConditions(singleStageFlashGORWorksheet: Worksheet, label: string, value: string, rowNo: number, borderStyle?: Partial<Borders>) {
            singleStageFlashGORWorksheet.getRow(rowNo).height = 15;
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.e + rowNo);
            const textGasOilRatioCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textGasOilRatioCell.value = label;
            textGasOilRatioCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textGasOilRatioCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) textGasOilRatioCell.border = borderStyle;

            singleStageFlashGORWorksheet.mergeCells(reportConstants.f + rowNo, reportConstants.gColumn + rowNo);
            const valueGasOilRatioCell = singleStageFlashGORWorksheet.getCell(reportConstants.f + rowNo);
            valueGasOilRatioCell.value = value;
            valueGasOilRatioCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            valueGasOilRatioCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) valueGasOilRatioCell.border = borderStyle;
        }

        function PopulateCalcPlusFractionData(singleStageFlashGORWorksheet: Worksheet, plusFracObj: PlusFractionResult, tableStartRow: number, borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; }; }) {
            PopulateFlashedFluidHeaders(singleStageFlashGORWorksheet, plusFracObj.compound, tableStartRow, borderStyle as Partial<Borders>);

            const molePercentFlashedGas = plusFracObj.compound.toLowerCase() === reportConstants.compc7Plus ? plusFracObj.molePercentFlashedGas?.toFixed(2) : '-';
            const molWeightFlashedGas = plusFracObj.compound.toLowerCase() === reportConstants.compc7Plus ? plusFracObj.molWeightFlashedGas : '-';
            const specificGravityFlashedGas = plusFracObj.compound.toLowerCase() === reportConstants.compc7Plus ? plusFracObj.specificGravityFlashedGas?.toFixed(3) : '-';

            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.molePercent, (plusFracObj.molePercentFlashedLiquid ?? 0)?.toFixed(2).toString(), (molePercentFlashedGas ?? 0.00).toString(), (plusFracObj.molePercentFlashedWholeFluid ?? 0)?.toFixed(2).toString(), tableStartRow + 1, borderStyle as Partial<Borders>);
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.moleWeight, (plusFracObj.molWeightFlashedLiquid ?? 0).toString(), (molWeightFlashedGas ?? 0).toString(), (plusFracObj.molWeightFlashedWholeFluid ?? 0).toString(), tableStartRow + 2, borderStyle as Partial<Borders>);
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.specificGravitySubString, (plusFracObj.specificGravityFlashedLiquid ?? 0)?.toFixed(3).toString(), (specificGravityFlashedGas ?? 0.000).toString(), (plusFracObj.specificGravityFlashedWholeFluid ?? 0)?.toFixed(3).toString(), tableStartRow + 3, borderStyle as Partial<Borders>);
        }

        function PopulateFlashedFluidHeaders(singleStageFlashGORWorksheet: Worksheet, compound: string, tableStartRow: number, borderStyle: Partial<Borders>) {
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + tableStartRow, reportConstants.e + tableStartRow);
            const textC36PlusCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + tableStartRow);
            textC36PlusCell.value = compound;
            textC36PlusCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textC36PlusCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.getColumn(reportConstants.f).width = 15;
            const textFlashedLiquidCell = singleStageFlashGORWorksheet.getCell(reportConstants.f + tableStartRow);
            textFlashedLiquidCell.value = reportConstants.flashedLiquid;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            singleStageFlashGORWorksheet.getColumn(reportConstants.gColumn).width = 15;
            const textFlashedGasCell = singleStageFlashGORWorksheet.getCell(reportConstants.gColumn + tableStartRow);
            textFlashedGasCell.value = reportConstants.flashedGas;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            singleStageFlashGORWorksheet.getColumn(reportConstants.h).width = 15;
            const textWholeFluidCell = singleStageFlashGORWorksheet.getCell(reportConstants.h + tableStartRow);
            textWholeFluidCell.value = reportConstants.wholeFluid;
            textWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textWholeFluidCell.border = borderStyle;
        }

        function PopulateFlashedFluidValues(singleStageFlashGORWorksheet: Worksheet, content: string, flashedLiquid: string, flashedGas: string, wholeFluid: string, tableStartRow: number, borderStyle?: Partial<Borders>) {
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + tableStartRow, reportConstants.e + tableStartRow);
            const textMolePercentCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + tableStartRow);
            textMolePercentCell.value = content;
            textMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) textMolePercentCell.border = borderStyle;

            const molePercentFlashedLiquidCell = singleStageFlashGORWorksheet.getCell(reportConstants.f + tableStartRow);
            molePercentFlashedLiquidCell.value = flashedLiquid;
            molePercentFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            molePercentFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) molePercentFlashedLiquidCell.border = borderStyle;

            const molePercentFlashedGasCell = singleStageFlashGORWorksheet.getCell(reportConstants.gColumn + tableStartRow);
            molePercentFlashedGasCell.value = flashedGas;
            molePercentFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            molePercentFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) molePercentFlashedGasCell.border = borderStyle;

            const molePercentWholeFluidCell = singleStageFlashGORWorksheet.getCell(reportConstants.h + tableStartRow);
            molePercentWholeFluidCell.value = wholeFluid;
            molePercentWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            molePercentWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) molePercentWholeFluidCell.border = borderStyle;
        }
    }

    private GetCellValue(cell: TableCell): string | number {
        if (typeof cell === 'object' && cell !== null && 'content' in cell) {
            return cell.content;
        }

        return cell ?? '';
    }

    // Applies a matching decimal numFmt to composition percentages, treating 0 as blank like the previous logic.
    private SetCompositionCellValue(cell: import('exceljs').Cell, rawCellData: TableCell): void {
        const unwrapped = this.GetCellValue(rawCellData);
        if (unwrapped !== '' && !isNaN(Number(unwrapped)) && Number(unwrapped) === 0) {
            cell.value = '';
            return;
        }
        setNumericOrTextCellValue(cell, unwrapped);
    }

    private AddOuterBorder(worksheet: import('exceljs').Worksheet, startRowNo: number, endRowNo: number, startColumnNo: number, endColumnNo: number) {
        for (let rowNo = startRowNo; rowNo <= endRowNo; rowNo++) {
            for (let columnNo = startColumnNo; columnNo <= endColumnNo; columnNo++) {
                // Merged cells only accept style changes on their master (top-left) cell
                const cell = worksheet.getCell(rowNo, columnNo).master;
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                cell.border = {
                    top: rowNo === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: rowNo === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: columnNo === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: columnNo === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }
}

